import { css, cx } from "@emotion/css";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { ListItemLink, List, SearchInput, Pagination, InlineEmptyStateOnboarding, ListTitle, ListDescription, CodeDecorator, formatExternalUrl } from "@octopusdeploy/design-system-components";
import { TimeSpanHelper } from "@octopusdeploy/design-system-octopus-components";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery, useMutation } from "@octopusdeploy/octopus-react-client";
import type { SubscriptionResource, ResourceCollection, GetTeamsLookupBffResponseTeam, Repository } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { keyBy } from "lodash";
import { useCallback } from "react";
import { TeamChip, DisabledChip } from "~/components/Chips/index";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
interface SubscriptionsPageProps {
    loaderData: SubscriptionsPageLoaderData;
    queryParams: SubscriptionsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<SubscriptionsPageQueryParams>;
}
interface SubscriptionsPageLoaderData {
    subscriptions: ResourceCollection<SubscriptionResource>;
    hasSubscriptions: boolean;
    teams: GetTeamsLookupBffResponseTeam[];
}
interface SubscriptionsPageQueryParams {
    partialName: string;
    page: number | undefined;
    pageSize: number | undefined;
}
export async function subscriptionsPageLoader(repository: Repository, queryParams: SubscriptionsPageQueryParams) {
    const subscriptionsAsync = getSubscriptions(repository, queryParams);
    const teamsAsync = repository.Teams.lookupBff();
    return {
        ...(await subscriptionsAsync),
        teams: await teamsAsync,
    };
}
async function getSubscriptions(repository: Repository, { partialName = "", page = 1, pageSize = defaultPageSize }: SubscriptionsPageQueryParams) {
    const subscriptions = repository.Subscriptions.list({ partialName, take: pageSize, skip: (page - 1) * pageSize });
    const subscriptionsStats = repository.Subscriptions.list({
        partialName: partialName,
        skip: (page - 1) * pageSize,
        take: pageSize,
    });
    return {
        subscriptions: await subscriptions,
        hasSubscriptions: (await subscriptionsStats).TotalResults > 0,
    };
}
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
const pageTitle = "Subscriptions";
export function SubscriptionsPage({ loaderData, queryParams, setQueryParams }: SubscriptionsPageProps) {
    const teamsLookup = keyBy(loaderData.teams, (t) => t.Id);
    const [debouncedSubscriptionName, setDebouncedSubscriptionName] = useDebouncedExternalState(queryParams.partialName, (value) => setQueryParams({ ...queryParams, partialName: value, page: 1 }), 200);
    const { result: subscriptionsResult, refetch: refetchSubscriptions } = useQuery((repository) => getSubscriptions(repository, { partialName: queryParams.partialName, page: queryParams.page, pageSize: queryParams.pageSize }), [queryParams.partialName, queryParams.page, queryParams.pageSize], "Load subscriptions list", {
        initialResult: loaderData,
    });
    const { subscriptions, hasSubscriptions } = subscriptionsResult;
    const { execute: updateSubscription } = useMutation({
        name: "Update subscription",
        action: async (repository: Repository, subscription: SubscriptionResource) => await repository.Subscriptions.save(subscription),
        afterComplete: async () => refetchSubscriptions(),
    });
    const addSubscriptionPageAction: PrimaryPageAction = {
        type: "navigate",
        hasPermissions: isAllowed({ permission: Permission.SubscriptionCreate }),
        label: "Add Subscription",
        path: links.createSubscriptionPage.generateUrl(),
    };
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    if (!hasSubscriptions) {
        return (<PageContent header={{ title: pageTitle, primaryAction: addSubscriptionPageAction }}>
                <InlineEmptyStateOnboarding illustration={"NoEntities"} entityName={"Subscriptions"} actions={{ primary: addSubscriptionPageAction }} learnMore={{ label: "Docs: Subscriptions", path: formatExternalUrl("Subscriptions") }}/>
            </PageContent>);
    }
    return (<PageContent header={{ title: pageTitle, primaryAction: addSubscriptionPageAction }} filters={{ inputs: [<SearchInput value={debouncedSubscriptionName} onChange={setDebouncedSubscriptionName} accessibleName={"Filter subscriptions by name"} placeholder="Filter by name"/>] }} pagination={{
            ui: (<Pagination label="Subscriptions" totalResults={subscriptions.TotalResults} itemsPerPageOptions={pageSizeOptions} selectedItemsPerPage={subscriptions.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: "topAndBottom",
        }}>
            <List items={subscriptions.Items} renderRow={(props) => <SubscriptionRow subscription={props.item} teamsLookup={teamsLookup} updateSubscription={updateSubscription}/>}/>
        </PageContent>);
}
function SubscriptionRow({ subscription, teamsLookup, updateSubscription }: {
    subscription: SubscriptionResource;
    teamsLookup: Record<string, GetTeamsLookupBffResponseTeam>;
    updateSubscription: (newValue: SubscriptionResource) => void;
}) {
    const emailTeams = subscription.EventNotificationSubscription.EmailTeams.map((id) => teamsLookup[id]).filter((t) => !!t);
    const emailFrequencyPeriodText = TimeSpanHelper.timeSpanTextToHumanReadableString(subscription.EventNotificationSubscription.EmailFrequencyPeriod);
    const webhookTeams = subscription.EventNotificationSubscription.WebhookTeams.map((id) => teamsLookup[id]).filter((t) => !!t);
    const toggleEnabledStatus = () => {
        const newValue = { ...subscription, IsDisabled: !subscription.IsDisabled };
        updateSubscription(newValue);
    };
    const renderDescription = emailTeams.length > 0 || subscription.EventNotificationSubscription.WebhookURI;
    return (<ListItemLink href={links.subscriptionPage.generateUrl({ subscriptionId: subscription.Id })}>
            <div className={styles.listContent}>
                <div className={styles.titleAndDescriptionContainer}>
                    <ListTitle>
                        {subscription.Name}
                        {subscription.IsDisabled && (<span className={styles.disabledChip}>
                                <DisabledChip />
                            </span>)}
                    </ListTitle>
                    <div className={cx(styles.description, { [styles.disabled]: subscription.IsDisabled })}>
                        {renderDescription && (<ListDescription description={<>
                                        {emailTeams.length > 0 && (<p className={styles.descriptionItem}>
                                                Email the teams{" "}
                                                <span className={styles.teamChipList}>
                                                    {emailTeams.map((t) => (<TeamChip key={t.Id} team={t}/>))}
                                                </span>
                                                every {emailFrequencyPeriodText}
                                            </p>)}

                                        {subscription.EventNotificationSubscription.WebhookURI && (<p className={styles.descriptionItem}>
                                                Send a POST to webhook <CodeDecorator value={subscription.EventNotificationSubscription.WebhookURI}/>
                                                {webhookTeams.length > 0 && (<span>
                                                        {" "}
                                                        with events scoped to those viewable by teams:{" "}
                                                        {webhookTeams.map((t) => (<TeamChip key={t.Id} team={t}/>))}
                                                    </span>)}
                                            </p>)}
                                    </>}/>)}
                    </div>
                </div>
                <OverflowMenu menuItems={[OverflowMenuItems.item(subscription.IsDisabled ? "Enable" : "Disable", toggleEnabledStatus, { permission: Permission.SubscriptionEdit })]}/>
            </div>
        </ListItemLink>);
}
const styles = {
    listContent: css({
        display: "flex",
        padding: space[16],
        alignItems: "center",
    }),
    titleAndDescriptionContainer: css({
        flex: 1,
    }),
    description: css({
        color: themeTokens.color.text.primary,
    }),
    descriptionItem: css({
        margin: 0,
    }),
    disabled: css({
        color: themeTokens.color.text.disabled,
    }),
    disabledChip: css({
        marginLeft: space[2],
    }),
    teamChipList: css({
        display: "inline-flex",
        flexWrap: "wrap",
        gap: space[2],
    }),
};
